import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { samplePages } from '../data/landing';
import { SectionHeading } from './SectionHeading';
export function SampleCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, samplePages.length - 1));
    const slide = track.children[clamped] as HTMLElement | undefined;
    const first = track.children[0] as HTMLElement | undefined;
    if (!slide || !first) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: slide.offsetLeft - first.offsetLeft,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, []);
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const first = track.children[0] as HTMLElement | undefined;
    if (!first) return;
    let closest = 0;
    let distance = Infinity;
    Array.from(track.children).forEach((child, index) => {
      const d = Math.abs((child as HTMLElement).offsetLeft - first.offsetLeft - track.scrollLeft);
      if (d < distance) {
        distance = d;
        closest = index;
      }
    });
    setActive(closest);
  };
  useEffect(() => {
    if (zoomed === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomed(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomed]);
  const atEnd = active >= samplePages.length - 1;
  const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors duration-150 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber';
  return <section className="bg-navy py-12 text-white md:py-24" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-160" data-mp="2e8w1s5now0q3">
      <div className="mx-auto max-w-[1180px] px-5 md:px-8" data-id="element-161" data-mp="36znu2s1mw585">
        <SectionHeading tone="dark" eyebrow="Look inside" title="See the pages" highlight="before you buy." description="A selection of interior pages from the pack. Swipe to browse and tap any page to read it full size." data-id="element-162" data-mp="36z4bra82netl" />

        <div ref={trackRef} onScroll={handleScroll} className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2" data-id="element-163" data-mp="2sytla8aoc28y">
          {samplePages.map((page, index) => <figure key={page.label} className="w-[72%] shrink-0 snap-start sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]" aria-label={`Sample ${index + 1} of ${samplePages.length}`} data-id="element-164" data-mp="1hk6gs6grmrzw" data-mpi={index}>
              <button type="button" onClick={() => setZoomed(index)} aria-label={`Enlarge: ${page.label}`} className="group relative block w-full overflow-hidden rounded-lg bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber" data-id="element-165" data-mp="2207vm5l50fz">
                <img src={page.image} alt={page.label} loading="lazy" className="aspect-[9/16] w-full object-cover object-top" data-id="element-166" data-mp="kr6tpi1oe860" />
                <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-navy/85 text-white transition-transform duration-150 ease-out group-hover:scale-110" data-id="element-167" data-mp="trmsie7h4r4j">
                  <ZoomInIcon className="h-4 w-4" aria-hidden="true" data-id="element-168" data-mp="2vkt60o3m7tb8" />
                </span>
              </button>
              <figcaption className="mt-3 text-center text-sm font-semibold text-white/85" data-id="element-169" data-mp="208bc1hmn7ogr">{page.label}</figcaption>
            </figure>)}
        </div>

        <div className="mt-8 flex items-center justify-center gap-5" data-id="element-170" data-mp="2a7958xv0k663">
          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous sample" className={arrowClass} data-id="element-171" data-mp="259h4dece59pr">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-172" data-mp="1hkeks0y756sm" />
          </button>
          <div className="flex gap-2" data-id="element-173" data-mp="1cll8gbunblu1">
            {samplePages.map((page, index) => <button key={page.label} type="button" onClick={() => goTo(index)} aria-label={`Go to sample ${index + 1}`} aria-current={active === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${active === index ? 'w-7 bg-amber' : 'w-2.5 bg-white/30 hover:bg-white/50'}`} data-id="element-174" data-mp="1r9pwzwgdqrkq" data-mpi={index} />)}
          </div>
          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Next sample" className={arrowClass} data-id="element-175" data-mp="3a8e2kr99te3u">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-176" data-mp="2wikam02gydpw" />
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-177" data-mp="30g8bdjquf0pp">
        {zoomed !== null && <motion.div role="dialog" aria-modal="true" aria-label={samplePages[zoomed].label} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-[60] overflow-y-auto bg-navy-deep/95" onClick={() => setZoomed(null)} data-id="element-178" data-mp="3o8ykll7d35wa">
            <button type="button" onClick={() => setZoomed(null)} aria-label="Close" className="fixed right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber" data-id="element-179" data-mp="23u4zvpqdehkd">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-180" data-mp="4klln8i7edy5" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} src={samplePages[zoomed].image} alt={samplePages[zoomed].label} onClick={e => e.stopPropagation()} className="mx-auto my-16 w-[92%] max-w-[640px] rounded-lg bg-white" data-id="element-181" data-mp="jru855jyh0i" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}